import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import type { ClientChat, ClientProjectSummary, UserIdentity } from './models';
import * as endpoints from './client';

/**
 * Query keys and hooks.
 *
 * Types are generated from the contract; hooks are hand-written. Generated
 * hooks bury the cross-entity invalidation, which is the actually hard part
 * here: posting a message changes the chat, the project list badge and the
 * composer state, and a generator has no way to know that.
 */

export const queryKeys = {
  me: ['client', 'me'] as const,
  projects: ['client', 'projects'] as const,
  chat: (projectId: number) => ['client', 'chat', projectId] as const,
};

export function useMe(enabled: boolean): UseQueryResult<UserIdentity, ApiError> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: endpoints.getMe,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useProjects(): UseQueryResult<ClientProjectSummary[], ApiError> {
  return useQuery({
    queryKey: queryKeys.projects,
    queryFn: endpoints.listProjects,
    // The list badges "waiting on you" and the preview state, both of which
    // move without this tab doing anything.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useChat(projectId: number | null): UseQueryResult<ClientChat, ApiError> {
  return useQuery({
    queryKey: queryKeys.chat(projectId ?? -1),
    queryFn: () => endpoints.getChat(projectId as number, { limit: 100 }),
    enabled: projectId !== null,
    // The stream is the live channel. Polling on top of it would double every
    // update and fight the ledger for the truth.
    refetchOnWindowFocus: true,
    staleTime: 2_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && [401, 403, 404].includes(error.httpStatus)) return false;
      return failureCount < 2;
    },
  });
}

export function usePostMessage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { text: string; attachment_ids?: string[]; client_message_id?: string }) =>
      endpoints.postMessage(projectId, body),
    onSuccess: () => {
      // The human turn and the new status arrive on the stream, but the
      // composer's own state (attachment budget, done availability) does not.
      void qc.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
      void qc.invalidateQueries({ queryKey: queryKeys.projects });
    },
    onError: () => {
      // A 409 means the composer's reason is now stale. Re-read it rather than
      // leaving the person looking at a box that says they may type.
      void qc.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
    },
  });
}

export function useAnswerAsk(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ askId, answer, decision }: { askId: string; answer: string; decision?: 'allow' | 'deny' }) =>
      endpoints.answerAsk(projectId, askId, { answer, decision }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
    },
  });
}

export function useMarkDone(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note?: string) => endpoints.markDone(projectId, note),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
      void qc.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

export function useUploadAttachment(projectId: number) {
  return useMutation({
    mutationFn: ({ file, note }: { file: File; note?: string }) =>
      endpoints.uploadAttachment(projectId, file, note),
  });
}
